import type { Me } from "@/sign-in/client.ts"
import { pathOfKey } from "@/projects/board.ts"

/*
 * What the shell says in words: who is signed in, as the account menu reads it, and what a
 * search typed into the command palette means.
 */

/**
 * The signed-in Member's standing on one line under their name: their role, then the groups
 * they are in. "Admin · NetLife AI", "Member · not in any group yet".
 */
export function describeStanding({ role, groups }: Pick<Me, "role" | "groups">) {
  const standing = role === "admin" ? "Admin" : "Member"
  const where =
    groups.length === 0 ? "not in any group yet" : groups.map(({ name }) => name).join(", ")
  return `${standing} · ${where}`
}

// A ticket key as someone types it to jump there: a Project's key of two to five letters, a dash
// and a number, in either case.
const typedTicketKey = /^[A-Z]{2,5}-\d+$/i

/**
 * The ticket a palette search names, when it is exactly a ticket's key ("vrf-42" jumps to VRF-42):
 * its key as Beacon writes it, and where its page is. Nothing for anything else.
 */
export function ticketJumpOf(query: string): { key: string; path: string } | undefined {
  const key = query.trim().toUpperCase()
  if (!typedTicketKey.test(key)) return undefined
  const path = pathOfKey(key)
  return path ? { key, path } : undefined
}

/**
 * Whether a palette entry answers a search: every word of it appears in one of the entry's texts,
 * in any case and any order, so "new tic" finds "New ticket in VRF". An empty search keeps all.
 */
export function answers(query: string, texts: readonly (string | undefined)[]) {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  const haystack = texts.filter(Boolean).join(" ").toLocaleLowerCase()
  return words.every((word) => haystack.includes(word))
}
